<template>
  <div class="medical-records" ref="medicalRecordPage">
    <portal to="page-name">Prontuário</portal>

    <div class="loading loading-lg" v-if="loadingPatient || loading" />
    <template v-else>
      <record-header
        :patient="patient"
        :loading="loadingPatient"
        :appointment="appointment"
      >
        <template v-slot:center>
          <template v-if="hasPendingRecord">
            <AIAssistantButton :is-enabled="true" @assistant-selected="handleAssistantSelected" />
          </template>
        </template>
        <template v-slot:right>
          <template v-if="hasPendingRecord">
            <div class="medical-record-timer" v-if="!triage">
              <span>{{ elapsedTime }}</span>
              <span v-if="isPaused" class="in-pause">(em pausa)</span>
            </div>
            <button
              type="button"
              class="btn btn-lg btn-gray btn-icon tooltip-bottom"
              :class="{ loading: canceling, tooltip: !canceling }"
              :disabled="isProcessing"
              :data-tooltip="canceling ? '' : 'Cancelar atendimento'"
              @click="cancel"
            ><fa-icon :icon="['fal', 'trash-alt']" /></button>
            <template v-if="!triage">
              <button
                v-if="isPaused"
                type="button"
                class="btn btn-lg btn-info tooltip-bottom"
                :class="{ loading: resuming, tooltip: !resuming }"
                :disabled="isProcessing"
                @click="resume"
                data-tooltip="Continuar atendimento"
              ><fa-icon :icon="['fal', 'play']" /></button>
              <button
                v-else
                type="button"
                class="btn btn-lg btn-info tooltip-bottom"
                :class="{ loading: pausing, tooltip: !pausing }"
                :disabled="isProcessing"
                @click="pause"
                data-tooltip="Pausar atendimento"
              ><fa-icon :icon="['fal', 'pause']" /></button>
            </template>
            <button
              v-if="!triage"
              type="button"
              class="btn btn-lg btn-error"
              :class="{ loading: isLoading }"
              :disabled="isLoading || isProcessing"
              @click="finishing"
            >Finalizar</button>
            <button
              v-else
              type="button"
              class="btn btn-info btn-lg tooltip tooltip-bottom"
              @click="finishTriage"
              data-tooltip="Finalizar"
            >Finalizar</button>
          </template>
          <template v-else>
            <button
              type="button"
              class="btn btn-lg btn-primary"
              :class="{ loading: isLoading }"
              :disabled="isLoading"
              @click="start"
            >Iniciar atendimento</button>
            <button
              v-if="hasPCSModule"
              type="button"
              class="btn btn-icon btn-gray-outline btn-lg tooltip-bottom"
              :class="{ loading: pcsCalling, tooltip: !pcsCalling }"
              :disabled="!patient.id"
              data-tooltip="Chamar paciente"
              @click="callPatient"
            >
              <fa-icon :icon="['fal', 'bullhorn']"  />
            </button>
            <template>
              <button
                v-if="!triage"
                type="button"
                class="btn btn-icon btn-gray-outline btn-lg tooltip tooltip-bottom"
                :disabled="records.length === 0"
                data-tooltip="Imprimir"
                @click="showPrint"
              ><fa-icon :icon="['fal', 'print']" /></button>
            </template>
          </template>
          <button
            type="button"
            class="btn btn-icon btn-gray-outline btn-lg tooltip tooltip-bottom"
            data-tooltip="Voltar"
            @click="$router.back()"
          ><fa-icon :icon="['fal', 'arrow-left']" /></button>
        </template>
      </record-header>

      <div class="modal" :class="{ active: showAIAssistantModal }" v-if="showAIAssistantModal">
        <a href="#close" class="modal-overlay" @click="closeAIAssistantModal"></a>
        <div class="modal-container modal-lg">

          <div>
            <noa-notes-widget
              v-if="selectedAssistant === 'noa-notes'"
              :patient-id="patient.id"
              :medical-record-id="form.id"
              :is-enabled="true"
              @summarized="handleNoaNotesSummarized"
            />
          </div>
        </div>
      </div>

      <div class="page-container">
        <template v-if="hasPendingRecord">
          <div
            v-if="isPending || isTriage || triage"
            class="card card-page medical-record-form"
          >
            <st-tabs id="medical-record-tabs" ref="medical-record-tabs" @changed="changeTabs">
              <st-tab id="tab-anamnesis" :name="triage ? 'Triagem' : 'Anamnese / evolução'">
                <anamnesis-form
                  v-if="form.id"
                  :patient="patient"
                  :data="form"
                  :pending="pending"
                  :appointment="appointment"
                  :expenses="appointment.expenses"
                  :history="history"
                  :printing="printing.anamnesis"
                  @print="printAnamnesis"
                />
              </st-tab>
              <st-tab
                v-if="!triage && (!patient.hospitalization || !patient.hospitalization.id)"
                id="tab-request"
                name="Solicitação"
              >
                <request-form
                  v-if="form.id"
                  :patient="patient"
                  :appointment="appointment"
                  :medicalRecordId="form.id"
                  @scroll-top="scrollTop"
                />
              </st-tab>
              <st-tab
                v-if="!triage && patient.hospitalization && patient.hospitalization.id"
                id="tab-prescriptions"
                name="Prescrição"
              >
                <prescription-form
                  v-if="form.id"
                  :medicalRecordId="form.id"
                  :patient="patient"
                  :patientId="patient.id"
                  @scroll-top="scrollTop"
                />
              </st-tab>
              <st-tab v-if="!triage" id="tab-recipes" name="Receituário">
                <recipe-form
                  v-if="form.id"
                  :medicalRecordId="form.id"
                  :patient="patient"
                  :patientId="patient.id"
                  @scroll-top="scrollTop"
                />
              </st-tab>
              <st-tab v-if="!triage" id="tab-documents" name="Impressos">
                <document-form
                  :medicalRecordId="form.id"
                  :patient="patient"
                  v-if="form.id"
                  @scroll-top="scrollTop"
                />
              </st-tab>
              <st-tab id="tab-exams" name="Exames" v-if="/Stenci|Canto|Oap/.test(branch.name)">
                <exam-form
                  v-if="form.id"
                  :medicalRecordId="form.id"
                  :patient="patient"
                  :expenses="appointment.expenses"
                  @scroll-top="scrollTop"
                />
              </st-tab>
              <st-tab v-if="!triage" id="tab-attachment" name="Anexos">
                <st-entity-file-manager
                  v-if="form.id"
                  :entity-id="patient.id"
                  :origin-id="form.id"
                  origin-type="medical_record"
                  :list-all="listAllFiles"
                  :key="form.id"
                  @loadingFiles="loadingFiles"
                  :readonly="isReadonly"
                />
                <button
                  class="btn btn-neutral mt-2"
                  :class="{ loading: loadingAllFiles }"
                  :disabled="loadingAllFiles"
                  @click="listAllFiles = !listAllFiles"
                >
                  <span v-if="listAllFiles">Ocultar outros anexos</span>
                  <span v-else>Listar outros anexos</span>
                </button>
              </st-tab>
            </st-tabs>
          </div>

          <div class="medical-record-paused" v-else-if="isPaused">
            <h3 class="paused-title">Atendimento em pausa</h3>

            <item-form :item="pendingRecord">
              <template v-slot:footer>
                <button
                  type="button"
                  class="btn btn-info btn-icon btn-icon-left"
                  :class="{ loading: resuming }"
                  @click="resume"
                  :disabled="resuming"
                ><fa-icon :icon="['fal', 'play']" /> Continuar atendimento</button>
              </template>
            </item-form>
          </div>
        </template>

        <div
          class="hospitalization-info-group"
          v-if="patient.hospitalization && patient.hospitalization.id"
        >
          <fa-icon :icon="['fal', 'exclamation-triangle']" />
          <span class="mr-2">
            Paciente internado desde o dia {{ patient.hospitalization.date | date }} às
            {{ patient.hospitalization.date | date('time') }} </span
          >|
          <label class="form-switch ml-2">
            <input type="checkbox" v-model="filterHospitalizationRecords" />
            <i class="form-icon"></i>Exibir apenas os atendimentos do internamento atual
          </label>
        </div>

        <div class="empty mt-2" v-if="!hasPendingRecord && records.length === 0 && !triage">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="3x" />
          </div>
          <p class="empty-title h5">Paciente sem histórico</p>
          <p class="empty-subtitle">
            Nenhuma informação registrada deste paciente. Clique no botão abaixo para iniciar.
          </p>
          <button type="button" class="btn btn-primary" @click="start">Iniciar atendimento</button>
        </div>
        <div class="empty mt-2" v-else-if="!hasPendingRecord && triage">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="3x" />
          </div>
          <p class="empty-title h5">Triagem</p>
          <p class="empty-subtitle">
            Clique no botão abaixo para iniciar o pré-atendimento do paciente.
          </p>
          <button type="button" class="btn btn-primary" @click="start">Iniciar atendimento</button>
        </div>
        <template v-if="records.length > 0 && showHistory && !triage">
          <h3 class="previous-records-title" v-if="hasPendingRecord">Atendimentos anteriores</h3>

          <search-bar @search="search" />

          <template v-if="!detail.show">
            <item-form
              v-for="record in filteredRecords"
              :key="record.id"
              :item="record"
              @openDetail="openDetail"
            />
          </template>
          <detail :id="detail.id" @close="closeDetail" v-else />
        </template>
      </div>
    </template>

    <dx-modal
      title="Finalização"
      v-if="finish.show"
      :value="finish.show"
      @input="closeModalFinished"
      id="modal-finish"
      size="sm"
    >
      <label class="form-switch">
        <input type="checkbox" v-model="form.private" />
        <i class="form-icon" /> Definir atendimento como sigiloso
      </label>
      <label class="form-switch">
        <input type="checkbox" v-model="reminder" />
        <i class="form-icon" /> Lembre-me deste atendimento
      </label>
      <template v-if="reminder">
        <div class="divider" />
        <div class="columns">
          <div
            class="column col-12 form-group"
            :class="{ 'has-error': $v.form.reminder.date.$error }"
          >
            <label class="form-label">Data</label>
            <div class="input-group">
              <dx-dropdown
                class="input-group-addon"
                :items="period"
                label="text"
                @select="setPeriod"
                direction="left"
              ><fa-icon :icon="['fal', 'chevron-down']" /></dx-dropdown>
              <dx-input-date
                id="reminder-date"
                class="form-input text-center"
                v-model="form.reminder.date"
                placeholder="00/00/0000"
                @blur="$v.form.reminder.date.$touch()"
              />
            </div>
            <template v-if="$v.form.reminder.date.$error">
              <div class="form-input-hint" v-if="!$v.form.reminder.date.required">
                Campo obrigatório
              </div>
              <div class="form-input-hint" v-else-if="!$v.form.reminder.date.date">
                Data inválida
              </div>
              <div class="form-input-hint" v-else-if="!$v.form.reminder.date.minDate">
                Data inválida
              </div>
            </template>
          </div>
        </div>
      </template>
      <template slot="footer">
        <button
          class="btn btn-error btn-block"
          :class="{ loading: this.saving }"
          :disabled="this.saving"
          @click="finished"
          v-focus
        >Finalizar</button>
      </template>
    </dx-modal>
    <dx-modal title="Imprimir" v-model="print.show" v-if="print.show" id="modal-print" size="sm">
      <label class="form-checkbox print-check">
        <input type="checkbox" v-model="print.justMe" />Apenas meus atendimentos
        <i class="form-icon" />
      </label>
      <div class="medical-records-print-item" v-for="(print, p) in filteredPrintRecords" :key="p">
        <span>{{ print.date | date("DD/MM/YYYY HH:mm") }}</span
        ><br />
        <span>{{ print.professionalName }}</span>
        <label class="form-checkbox print-check">
          <input type="checkbox" v-model="print.selected" />
          <i class="form-icon" />
        </label>
      </div>
      <template slot="footer">
        <button
          class="btn btn-primary mr-1"
          :disabled="print.printing"
          :class="{ loading: print.printing }"
          @click="printMultiple"
        >
          Imprimir
        </button>
        <button class="btn" @click="print.show = false">Sair</button>
      </template>
    </dx-modal>
    <patient-alert
      v-if="patientAlertModal.show"
      :patient-id="patient.id"
      :patient-name="patient.name"
      :external-data="patientAlertModal.data"
      :show="patientAlertModal.show"
      @close="patientAlertModal.show = false"
    />
    <config-modal v-if="hasPCSModule"></config-modal>
  </div>
</template>

<script>
import { computed } from 'vue';
import moment from 'moment';
import formMixin from 'src/mixins/form';
import { mapState, mapGetters, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { mergeFrom } from '@/helpers/object';
import { pcsMainUrl } from '@/config';
import { toText } from '@/helpers/html';
import createSearch from '@/helpers/search';
import { durationFormat } from '@/helpers/date';
import RecordHeader from './record/Header.vue';
import AnamnesisForm from './anamnesis/Form.vue';
import DocumentForm from './document/Form.vue';
import RecipeForm from './recipe/Form.vue';
import PrescriptionForm from './prescription/Form.vue';
import RequestForm from './request/Form.vue';
import ExamForm from './exam/Form.vue';
import ItemForm from './ListItem.vue';
import { date, minDate } from '../../../data/validators';
import Detail from './DetailItem.vue';
import PatientAlert from '../../patient/modals/Alert.vue';
import ConfigModal from '../../attendance/components/modals/Config.vue';
import SearchBar from './SearchBar.vue';
import NoaNotesWidget from './NoaNotesWidget.vue';
import AIAssistantButton from './AIAssistantButton.vue';

const STATUSES = Object.freeze({
  PENDING: 'pending',
  PAUSED: 'paused',
  FINISHED: 'finished',
  TRIAGE: 'triage',
});

export default {
  mixins: [formMixin],
  components: {
    ConfigModal,
    RecordHeader,
    AnamnesisForm,
    DocumentForm,
    RecipeForm,
    PrescriptionForm,
    RequestForm,
    ItemForm,
    Detail,
    PatientAlert,
    ExamForm,
    SearchBar,
    NoaNotesWidget,
    AIAssistantButton,
  },
  data() {
    return {
      triage: this.$route.query.triage === 'true' || false,
      timer: '00:00:00',
      debounceTimeout: null,
      timerInterval: null,
      unwatch: null,
      saving: false,
      loading: false,
      loadingPatient: true,
      pcsCalling: false,
      pending: false,
      canceling: false,
      pausing: false,
      resuming: false,
      showHistory: true,
      reminder: false,
      showAIAssistantModal: false,
      selectedAssistant: null,
      listAllFiles: false,
      loadingAllFiles: false,
      idx: null,
      filter: {
        onlyMyRecords: false,
        search: '',
      },
      period: [
        { value: 7, text: '7 dias' },
        { value: 15, text: '15 dias' },
        { value: 30, text: '30 dias' },
        { value: 60, text: '60 dias' },
        { value: 90, text: '90 dias' },
        { value: 120, text: '120 dias' },
        { value: 180, text: '180 dias' },
        { value: 365, text: '365 dias' },
      ],
      filterHospitalizationRecords: false,
      detail: {
        show: false,
        id: '',
      },
      image: this.imageFormBlank(),
      appointment: {
        expenses: [],
        specialtyCode: 0,
        insurancePlanId: '',
        insuranceName: '',
      },
      patient: {
        id: '',
        name: '',
        socialName: '',
        gender: '',
        identity: {
          type: '',
          value: '',
        },
        birthDate: '',
        phone: '',
        cellphone: '',
        addresses: [],
        imageUrl: '',
        insuranceName: '',
        insuranceId: '',
        insurancePlanId: '',
        motherName: '',
        allergy: '',
        record: '',
        validity: '',
        hospitalization: {
          id: '',
          date: '',
        },
        profession: '',
      },
      records: [],
      finish: {
        show: false,
      },
      print: {
        show: false,
        justMe: false,
        printing: false,
        items: [],
      },
      printing: {
        anamnesis: false,
      },
      patientAlertModal: {
        show: false,
        data: null,
      },
      form: this.formBlank(),
      searcher: null,
      dirty: false,
    };
  },
  provide() {
    return {
      triage: this.triage,
      isReadonly: computed(() => this.isPaused),
    };
  },
  async mounted() {
    const patientId = this.$route.params.id;
    this.loadAppointment();
    await this.findPatient(patientId);
    await this.loadRecords(patientId);

    if (this.pendingRecord) {
      await this.load(this.pendingRecord.id).then(() => {
        this.setWatchers();
        this.startTimer();
      });
    } else {
      this.setWatchers();
    }
  },
  beforeDestroy() {
    this.unsetWatchers();
    if (this.dirty) {
      this.save(false).catch(this.showError);
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      branch: state => state.auth.user.branch,
      now: state => state.now,
    }),
    ...mapGetters({
      branchSettings: 'branchSettings',
      hasModule: 'hasModule',
    }),
    cidIsRequired() {
      return this.branchSettings && this.branchSettings['medicalRecord.cidRequired'];
    },
    isLoading() {
      return this.saving || this.pending;
    },
    isProcessing() {
      return this.saving || this.canceling || this.pausing || this.resuming;
    },
    history() {
      return this.records.filter(item => item.status !== 'pending');
    },
    bgImage() {
      if (!this.patient.gender) {
        return null;
      }

      let backgroundImage = `url('//static.stenci.pro/images/no-image-${this.patient.gender}.png')`;

      if (this.image.preview) {
        backgroundImage = `url('${this.image.preview}')`;
      } else if (this.patient.imageUrl) {
        backgroundImage = `url('${this.patient.imageUrl}?w=200&h=200')`;
      }

      return { backgroundImage };
    },
    showImageOptions() {
      if (this.image.showOptions) {
        return true;
      }
      return this.bgImage === null;
    },
    pendingRecord() {
      return this.records.find(({ status, professional }) => {
        if (status === STATUSES.TRIAGE) return true;
        if (professional.id !== this.user.id) return false;
        return [STATUSES.PENDING, STATUSES.PAUSED].includes(status);
      });
    },
    hasPendingRecord() {
      return !!this.pendingRecord;
    },
    isPending() {
      return this.form.status === STATUSES.PENDING;
    },
    isPaused() {
      return this.form.status === STATUSES.PAUSED;
    },
    isTriage() {
      return this.form.status === STATUSES.TRIAGE;
    },
    isReadonly() {
      return this.isPaused;
    },
    isNoaNotesEnabled() {
      return this.branchSettings && this.branchSettings['noaNotes.enabled'] === true;
    },
    filteredRecords() {
      let records = this.history;

      const search = this.filter.search.trim();
      if (this.searcher && (search || this.filter.onlyMyRecords)) {
        records = this.searcher.search(search);
      }

      if (
        this.patient.hospitalization
        && this.patient.hospitalization.id
        && this.filterHospitalizationRecords
      ) {
        return records.filter(item => item.hospitalizationId === this.patient.hospitalization.id);
      }

      return records;
    },
    filteredPrintRecords() {
      let records = this.print.items;
      if (this.print.justMe) {
        records = records.filter(item => item.professionalId === this.user.id);
      }
      return records;
    },
    hasPCSModule() {
      return this.hasModule('pcs');
    },
    elapsedTime() {
      const ms = this.form.events
        .filter(event => event.type === 'status')
        .reduce((acc, event, idx, events) => {
          if ([STATUSES.FINISHED, STATUSES.PAUSED].includes(event.value)) {
            return acc + moment(event.at).diff(events[idx - 1].at);
          }
          if (idx === (events.length - 1) && !this.isPaused) {
            return acc + moment(this.now).diff(event.at);
          }
          return acc;
        }, 0);

      return durationFormat(ms);
    },
  },
  validations() {
    const rules = {
      form: {
        reminder: {
          date: {},
        },
      },
    };

    if (this.reminder) {
      rules.form.reminder.date = {
        required,
        date,
        minDate: minDate(),
      };
    }

    return rules;
  },
  methods: {
    ...mapActions({
      loadAttendanceConfig: 'loadAttendanceConfig',
    }),
    loadingFiles(value) {
      this.loadingAllFiles = value;
    },
    loadAppointment() {
      const { appointmentId } = this.$route.query;

      if (appointmentId) {
        this.$httpX
          .get(`/appointments/${appointmentId}`)
          .then(({ data }) => {
            this.appointment.specialtyCode = 0;
            if (data.professional && data.professional.specialty) {
              this.appointment.specialtyCode = data.professional.specialty.code;
            }
            this.appointment.slot = data.slot;
            this.appointment.startDate = data.startDate;
            this.appointment.insurancePlanId = data.insurance.plan.id;
            this.appointment.insuranceName = this.getCustomName(data.insurance);
            this.appointment.expenses = data.expenses;
          })
          .catch(this.showError);
      }
    },
    setWatchers() {
      const watchData = () => ({
        anthropometry: this.form.anthropometry,
        vitalSigns: this.form.vitalSigns,
        cids: this.form.cids,
        anamnesis: this.form.anamnesis,
      });

      this.unwatch = this.$watch(watchData, {
        handler() {
          clearTimeout(this.debounceTimeout);
          this.debounceTimeout = setTimeout(() => {
            if (this.form.id) {
              this.pending = true;
              this.save(false).catch(this.showError);
            }
          }, 2000);
          this.dirty = true;
        },
        deep: true,
      });
    },
    unsetWatchers() {
      if (this.unwatch) {
        clearTimeout(this.debounceTimeout);
        this.unwatch();
        this.unwatch = null;
      }
      this.endTimer();
    },
    scrollTop() {
      this.$refs.medicalRecordPage.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    },
    setSearcher() {
      this.searcher = createSearch(this.history, {
        content: ({ anamnesis }) => Object.keys(anamnesis?.inputs || {})
          .map(key => toText(anamnesis.inputs[key]))
          .filter(Boolean)
          .join(' '),
        filter: item => (this.filter.onlyMyRecords ? item.professional.id === this.user.id : true),
      });
    },
    search(args) {
      this.filter.onlyMyRecords = args.onlyMyRecords;
      this.filter.search = args.search;
    },
    openDetail(id) {
      this.detail.id = id;
      this.detail.show = true;
    },
    closeDetail() {
      this.detail.id = '';
      this.detail.show = false;
    },
    async callPatient() {
      this.pcsCalling = true;
      try {
        const config = await this.loadAttendanceConfig();
        if (config) {
          const data = {
            place: config.placeId,
            patient: {
              name: this.patient.name,
            },
            room: {
              name: config.roomName,
            },
          };
          await this.$http.post(`${pcsMainUrl}/ticket/call`, data);
        }
      } catch (e) {
        this.showError(e);
      }
      this.pcsCalling = false;
    },
    handleAssistantSelected(assistant) {
      this.selectedAssistant = assistant;
      this.showAIAssistantModal = true;
    },
    closeAIAssistantModal() {
      this.showAIAssistantModal = false;
      this.selectedAssistant = null;
    },
    getNoaSummaryHtml(rawContent) {
      if (rawContent === undefined || rawContent === null) {
        return '';
      }
      if (typeof rawContent === 'object') {
        const htmlFromObject = this.stringifyNoaSummaryObject(rawContent);
        return htmlFromObject || '';
      }
      if (typeof rawContent !== 'string') {
        return String(rawContent);
      }
      const trimmedContent = rawContent.trim();
      if (trimmedContent === '') {
        return '';
      }
      let parsedContent = '';
      try {
        const parsed = JSON.parse(trimmedContent);
        parsedContent = this.stringifyNoaSummaryObject(parsed);
      } catch (error) {
        return trimmedContent;
      }
      return parsedContent || trimmedContent;
    },
    stringifyNoaSummaryObject(data) {
      if (!data || typeof data !== 'object') {
        return '';
      }
      return Object.entries(data)
        .filter(([, value]) => typeof value === 'string' && value.trim() !== '')
        .map(([label, value], index, array) => (
          this.composeNoaSummarySection(label, value, index, array.length)
        ))
        .join('');
    },
    composeNoaSummarySection(label, value, index, arrayLength) {
      const isLastItem = arrayLength - 1 === index;
      const labelText = typeof label === 'string' ? label.trim() : '';
      const content = value.trim();
      return `<div><strong>${labelText}:</strong> ${content}${isLastItem ? '' : '<p></p>'}</div>`;
    },
    handleNoaNotesSummarized(summary) {
      if (!summary) {
        this.$toast.show('Resumo do Noa Notes indisponível para preenchimento.', {
          type: 'warning',
        });
        return;
      }

      if (!this.form.anamnesis || !Array.isArray(this.form.anamnesis.inputs)) {
        this.$toast.show('Não foi possível preencher o prontuário. Template não carregado.', {
          type: 'error',
        });
        return;
      }

      if (!summary || summary.content === null) {
        this.$toast.show('Resumo do Noa Notes sem dados para preencher.', {
          type: 'warning',
        });
        return;
      }

      const processedContent = this.getNoaSummaryHtml(summary.content);

      const mappings = [
        { name: 'content', value: processedContent },
        { name: 'subjective', value: processedContent },
        { name: 'mainComplaint', value: processedContent },
      ];

      let updated = false;

      mappings.forEach(({ name, value }) => {
        if (value === undefined || value === null) {
          return;
        }
        const field = this.form.anamnesis.inputs.find(input => input.name === name);
        if (field) {
          field.value = value;
          updated = true;
        }
      });

      if (updated) {
        this.dirty = true;
        this.$toast.success('Resumo do Noa Notes aplicado ao prontuário.');
      } else {
        this.$toast.show('Campos do Noa Notes não encontrados no template atual.', {
          type: 'warning',
        });
      }
    },
    load(id) {
      this.loading = true;
      return this.$httpX
        .get(`/medical-records/${id}`)
        .then(({ data }) => {
          const mergeData = data || this.formBlank();
          mergeFrom(this.form, mergeData);
        })
        .catch(this.showError)
        .finally(() => {
          this.loading = false;
        });
    },
    loadRecords(patientId) {
      this.loading = true;
      return this.$httpX
        .get(`/patients/${patientId}/medical-records?limit=0`)
        .then(({ data }) => {
          this.records = data.items;
          this.setSearcher();
        })
        .catch(this.showError)
        .finally(() => {
          this.loading = false;
        });
    },
    loadAlert() {
      const params = {
        displayTo: 'medical_record',
      };

      this.$httpX
        .get(`/patients/${this.patient.id}/alerts`, { params })
        .then(({ data: result }) => {
          if (result && result.items && result.items.length > 0) {
            this.patientAlertModal.data = result;
            this.patientAlertModal.show = true;
          }
        })
        .catch(this.showError);
    },
    async validate() {
      if (!this.patient.insuranceId) {
        throw new Error('Paciente sem convênio definido. Verifique o cadastro do paciente');
      }
      if (!(await this.allowServiceWithoutAccount())) {
        throw new Error(
          'Não será possível iniciar o atendimento. Liberação não realizada na recepção.',
        );
      }
    },
    async start() {
      try {
        await this.validate();

        this.form = this.formBlank();
        this.form.startDate = moment().format('YYYY-MM-DDTHH:mm:ss');
        this.form.start = true;
        this.reminder = false;

        await this.save(false);
        this.startTimer();
        this.scrollTop();
      } catch (e) {
        this.showError(e);
      } finally {
        this.saving = false;
      }
    },
    cancel() {
      this.$dialog.show('', {
        html: `<div class="text-center">
          <h5 class="text-center">Atenção!</h5>
          <div>Deseja realmente cancelar este atendimento?</div>
          </div>`,
        buttons: [
          {
            label: 'Não',
            classes: '',
          },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.canceling = true;
              this.unsetWatchers();
              this.delete(this.form.id)
                .then(() => {
                  const record = this.records.find(item => item.id === this.form.id);
                  const index = this.records.indexOf(record);
                  this.records.splice(index, 1);
                })
                .catch(this.showError)
                .then(() => {
                  close();
                  this.closeDetail();
                  this.canceling = false;
                  this.showHistory = true;
                  this.$router.back();
                });
            },
          },
        ],
      });
    },
    async createOrUpdate(data) {
      const { data: responseData } = !this.form.id
        ? await this.$httpX.post('/medical-records', data)
        : await this.$httpX.put(`/medical-records/${this.form.id}`, data);

      this.form.id = responseData.id;
      this.form.status = responseData.status;
      this.form.events = responseData.events;

      const found = this.records.find(item => item.id === responseData.id);
      if (found) {
        mergeFrom(found, responseData);
        found.anamnesis.inputs = responseData.anamnesis.inputs;
      } else {
        this.records.unshift(responseData);
      }
      return responseData;
    },
    async pause() {
      this.pausing = true;
      try {
        const postData = {
          status: STATUSES.PAUSED,
          anamnesis: {
            ...this.form.anamnesis,
            inputs: this.form.anamnesis.inputs.reduce((acc, item) => {
              acc[item.name] = item.value;
              return acc;
            }, {}),
          },
        };
        clearTimeout(this.debounceTimeout);
        await this.createOrUpdate(postData);
      } catch (e) {
        this.$toast.error(e);
      }
      this.pausing = false;
    },
    async resume() {
      this.resuming = true;
      try {
        const postData = {
          status: STATUSES.PENDING,
        };
        clearTimeout(this.debounceTimeout);
        await this.createOrUpdate(postData);
      } catch (e) {
        this.$toast.error(e);
      }
      this.resuming = false;
    },
    finishing() {
      if (this.cidIsRequired && this.form.cids.length === 0) {
        this.$toast.error('O campo CID é obrigatório!');
        return;
      }
      this.reminder = false;
      this.form.reminder.date = '';
      this.$v.form.reminder.$reset();
      this.form.private = this.getPrivateStatus();
      this.finish.show = true;
      this.showHistory = true;
    },
    finished() {
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return;
      }
      this.unsetWatchers();
      this.save(true)
        .then(() => {
          this.closeDetail();
          this.showHistory = true;
          this.finish.show = false;
          this.$router.back();
        })
        .catch(this.showError);
    },
    async finishTriage() {
      this.unsetWatchers();
      try {
        await this.save(true);
        this.$router.back();
      } catch (e) {
        this.$toast.error(e);
      }
    },
    async save(finished) {
      this.$v.form.$touch();
      if (this.saving || this.$v.form.$error) {
        return false;
      }

      const queryParams = this.$route.query || {};

      this.saving = true;

      const data = this.clone(this.form);
      if (!data.appointmentId && queryParams.appointmentId) {
        data.appointmentId = queryParams.appointmentId;
      }

      if (this.patient.hospitalization && this.patient.hospitalization.id) {
        data.hospitalizationId = this.patient.hospitalization.id;
      }

      data.status = finished ? 'finished' : 'pending';
      data.endDate = finished ? moment().format('YYYY-MM-DDTHH:mm') : '';
      data.professionalId = this.user.id;
      data.patientId = this.patient.id;
      data.insuranceId = this.patient.insuranceId || null;

      if (this.triage) {
        data.triage = {
          notes: this.triage.notes,
        };
        data.professionalId = queryParams.professionalId;
      } else {
        delete data.triage;
      }

      data.anamnesis.inputs = data.anamnesis.inputs.reduce((acc, item) => {
        acc[item.name] = item.value;
        return acc;
      }, {});

      try {
        const result = await this.createOrUpdate(data);
        if (finished) {
          this.$toast.show('Atendimento salvo com sucesso');
        }
        return result;
      } catch (e) {
        if (e.response?.data) {
          e.message = e.response.data.message || e.message;
          delete e.response;
        }

        e.message = [
          'Ocorreu um erro ao salvar este registro. Tente novamente!',
          `[${e.message}]`,
        ].join('\n');

        throw e;
      } finally {
        this.saving = false;
        this.pending = false;
        this.dirty = false;
      }
    },
    delete(id) {
      return this.$httpX
        .delete(`/medical-records/${id}`)
        .then(({ data }) => data)
        .catch(this.showError);
    },
    closeModalFinished() {
      this.finish.show = false;
    },
    startTimer() {
      this.setTimer();
      this.timerInterval = setInterval(() => {
        this.setTimer();
      }, 1000);
    },
    endTimer() {
      clearInterval(this.timerInterval);
      this.timer = '00:00:00';
    },
    setTimer() {
      this.timer = this.$timeDiff(this.form.startDate);
    },
    findPatient(patientId) {
      this.loadingPatient = true;
      return this.$httpX
        .get(`/patients/${patientId}`)
        .then(({ data }) => {
          this.patient = {
            id: data.id,
            name: data.name,
            socialName: data.socialName,
            birthDate: data.birthDate,
            gender: data.gender,
            phone: data.phone,
            cellphone: data.cellphone,
            identity: data.identity,
            imageUrl: data.imageUrl,
            addresses: data.addresses,
            motherName: data.patient && data.patient.motherName ? data.patient.motherName : '',
            insuranceName:
              data.patient && data.patient.insurance
                ? this.getCustomName(data.patient.insurance)
                : '',
            insuranceId: data.patient && data.patient.insurance ? data.patient.insurance.id : '',
            insurancePlanId:
              data.patient && data.patient.insurance && data.patient.insurance.plan
                ? data.patient.insurance.plan.id
                : '',
            allergy: data.patient && data.patient.allergy ? data.patient.allergy : false,
            record: data.patient && data.patient.insurance ? data.patient.insurance.record : '',
            validity: data.patient && data.patient.insurance ? data.patient.insurance.validity : '',
            hospitalization:
              data.patient && data.patient.hospitalization ? data.patient.hospitalization : null,
            profession: data.profession?.name || null,
          };
          this.loadAlert();
        })
        .finally(() => {
          this.loadingPatient = false;
        });
    },
    getCustomName(insurance) {
      if (insurance.plan && insurance.name !== insurance.plan.name) {
        return `${insurance.name} (${insurance.plan.name})`;
      }
      return insurance.name;
    },
    changeTabs({ tab }) {
      this.showHistory = tab.hash === 'tab-anamnesis';
    },
    imageFormBlank() {
      return {
        file: null,
        preview: null,
        showOptions: false,
        showWebCam: false,
      };
    },
    showPrint() {
      this.print.items = this.records.map(item => ({
        id: item.id,
        date: item.startDate,
        professionalId: item.professional.id,
        professionalName: item.professional.name,
        selected: true,
      }));
      this.print.show = true;
    },
    async printMultiple() {
      const ids = this.filteredPrintRecords.filter(({ selected }) => selected).map(({ id }) => id);

      if (!ids || ids.length === 0) {
        return;
      }

      this.print.printing = true;

      try {
        await this.$file.print('/medical-records/printMultiple', {
          ids: ids.join(','),
        });
      } catch (e) {
        this.showError(e);
      }

      this.print.printing = false;
    },
    async printAnamnesis() {
      this.printing.anamnesis = true;

      try {
        await this.save();
        await this.$file.print(`/medical-records/${this.form.id}/print`);
      } catch (e) {
        this.showError(e);
      }

      this.printing.anamnesis = false;
    },
    getPrivateStatus() {
      // 251510 - Psicólogo; 225133 - Psiquiatra
      // if (this.appointment.specialtyCode.toString() === '251510'
      //   || this.appointment.specialtyCode.toString() === '225133') {
      //   return true;
      // }
      return false;
    },
    allowServiceWithoutAccount() {
      if (
        this.user.branch.settings
        && this.user.branch.settings['clinical.blockServiceWithoutAccount']
      ) {
        const appointmentId = this.$route.query && this.$route.query.appointmentId
          ? this.$route.query.appointmentId
          : null;

        if (appointmentId) {
          return this.$http.get(`/appointments/${appointmentId}`).then(({ data }) => {
            if (data && Array.isArray(data.expenses) && data.expenses.length > 0) {
              return data.expenses && data.expenses.every(({ accountId }) => accountId);
            }
            return false;
          });
        }
        return false;
      }
      return true;
    },
    setPeriod({ value }) {
      this.form.reminder.date = moment().add(value, 'days').format('YYYY-MM-DD');
    },
    formBlank() {
      return {
        id: null,
        start: false,
        status: '',
        events: [],
        appointmentId: '',
        startDate: '',
        endDate: '',
        private: false,
        screeningId: '',
        professionalId: '',
        patientId: '',
        insurancePlanId: '',
        anthropometry: {
          cephalicPerimeter: '',
          abdominalCircumference: '',
          height: '',
          weight: '',
        },
        vitalSigns: {
          bloodPressure: '',
          respiratoryFrequency: '',
          heartRate: '',
          temperature: '',
          saturation: '',
        },
        reminder: {
          date: '',
          notes: '',
        },
        cids: [],
        anamnesis: {
          templateId: '',
          templateRenderer: null,
          inputs: [],
        },
      };
    },
    showError(e, options = {}) {
      options = { timeout: 15000, ...options };
      this.$toast.error(e, options);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.medical-records {
  $page-header-height: 4.2rem;

  .medical-record-header {
    background-color: $light-color;
    border-bottom: 1px solid $border-color;
    height: $page-header-height;
    position: sticky;
    top: 0;
    z-index: $zindex-0 + 1;
  }

  .pending-records {
    margin: $layout-spacing-xl;
  }

  .medical-record-form {
    padding: $layout-spacing-lg;
    margin-bottom: $layout-spacing;
    .tab {
      margin-bottom: $layout-spacing;

      a {
        padding-top: 0;
      }
    }
  }

  .medical-record-paused {
    border: $border-width solid $orange-color-300;
    border-radius: $border-radius;
    padding: $layout-spacing-lg;
    margin-bottom: $control-size-lg;

    .paused-title {
      color: $secondary-color-dark;
      font-size: $font-size-lg + $border-width;
      font-weight: 500;
      margin: $layout-spacing 0 $layout-spacing-lg 0;
      text-align: center;
    }

    .medical-record-item {
      border: $border-width dashed $orange-color-500;
      margin-bottom: 0;
      .card {
        background-color: rgba($orange-color-100, .2);
      }
    }
  }

  .medical-record-timer {
    font-size: 1rem;
    font-weight: 500;
    text-align: center;
    margin-right: $layout-spacing;
    .in-pause {
      color: $info-color;
      font-size: $font-size-lg;
      font-weight: 400;
      margin-left: $layout-spacing-sm;
    }
  }

  .field-display {
    margin-bottom: $layout-spacing;
    &:last-child {
      margin-bottom: 0;
    }
  }

  .field-display-value {
    border-left: $border-width solid $dark-blue-color-300;
    margin-left: $layout-spacing - $border-width;
    padding-left: $layout-spacing;
    table {
      width: 100%;
      border-collapse: collapse;
      th,
      td {
        border: $border-width solid $border-color-dark;
        padding: $layout-spacing-sm $layout-spacing;
      }
    }
    p,
    ul,
    ol {
      margin: $border-width 0 0 0;
      &:first-of-type {
        margin-top: 0;
      }
    }
    @media screen and (min-width: $size-lg) {
      table {
        width: auto;
        min-width: 50%;
      }
    }
  }

  .previous-records-title {
    color: $info-color;
    font-size: $font-size-lg + $border-width;
    font-weight: 500;
    margin: $layout-spacing-lg 0;
    text-align: center;
  }

  .hospitalization-info-group {
    align-items: center;
    background-color: lighten($info-color, 32%);
    border: $border-width solid $info-color;
    border-radius: $border-radius;
    flex-direction: row;
    display: flex;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing-sm $layout-spacing;
    span {
      font-weight: 600;
    }
    svg {
      color: $info-color;
      font-size: 0.7rem;
      width: 1.2rem;
    }
  }

  p {
    margin: 0;
  }
}

.medical-records-print-item {
  background-color: lighten($gray-color-light, 5%);
  border: $border-width solid $border-color;
  border-radius: $border-radius;
  position: relative;
  margin: $layout-spacing-sm;
  padding: $layout-spacing-sm;

  .print-check {
    position: absolute;
    top: 0.4rem;
    right: 0;
  }
}
</style>
